import { useEffect, useRef } from "react"
import { useAgentStore } from "@/stores/agent-store"

/**
 * The fixed opening utterance for a fresh Excel schedule fill thread. The
 * assistant's first turn is an orientation pass (inspect the workbook, describe
 * each sheet, propose a fill plan) — asking for it costs the user a message they
 * would otherwise have to type, so the page asks on their behalf.
 */
export const SCHEDULE_SEED_UTTERANCE =
  "Inspect this workbook and propose how we should fill it."

/**
 * Send the seed turn exactly once for an empty fill thread.
 *
 * Every guard is load-bearing:
 * - no session, or a session scoped to a different artifact → the AI panel has
 *   not initialised for THIS workbook yet (it mounts and calls `initSession`),
 *   so sending now would post into someone else's thread. `initSession` sets
 *   `activeScope` and the hydrated `messages` in one commit, so a matching scope
 *   also means the history has landed — an empty `messages` here really is an
 *   empty thread, not a half-loaded one.
 * - a non-empty thread → the user (or a previous visit) already started it.
 * - a turn in flight → never queue behind one.
 * - the ref key → StrictMode's double-effect, and any re-render, send once per
 *   session + thread.
 */
export function useScheduleSeedTurn(artifactId: string | null): void {
  const sessionId = useAgentStore((s) => s.sessionId)
  const scopeArtifactId = useAgentStore((s) => s.activeScope?.artifactId ?? null)
  const threadId = useAgentStore((s) => s.activeThreadId)
  const messageCount = useAgentStore((s) => s.messages.length)
  const isBusy = useAgentStore((s) => s.isStreaming || s.isPolling)
  const seededKeyRef = useRef<string | null>(null)

  useEffect(() => {
    if (!artifactId || !sessionId) return
    if (scopeArtifactId !== artifactId) return
    if (messageCount > 0 || isBusy) return

    const key = `${sessionId}:${threadId ?? ""}`
    if (seededKeyRef.current === key) return
    seededKeyRef.current = key
    void useAgentStore.getState().sendTurn(SCHEDULE_SEED_UTTERANCE)
  }, [artifactId, sessionId, scopeArtifactId, threadId, messageCount, isBusy])
}
